import type { Libro, Movimiento } from "../api/tipos.ts";
import { esDeCerteza } from "../api/tipos.ts";
import { hoyLocal } from "./caja-modelo.ts";

/**
 * Las tres cifras del Resumen (SPEC-029 §16, M-13).
 *
 * Antes la primera pantalla tenía un cuadre del mes, una rosca de autonomía y
 * la cartera por estados: tres respuestas a preguntas que el dueño no se hace
 * al abrirla. La que se hace es «¿cómo va hoy y qué me toca?», y se contesta
 * con tres números.
 */
export interface CifrasDeHoy {
  /** Lo que entró hoy al banco que es pago de alguien. */
  entro: { centavos: number; pagos: number };
  /** Lo que espera una decisión, de cualquier día: es trabajo, no historia. */
  faltaDecir: { centavos: number; pagos: number; deHoy: number };
  /** Saldo de la cartera. `null` para quien no la ve. */
  faltaCobrar: number | null;
}

/**
 * Un pago que alguien descartó —«no es un pago nuestro»: un traslado entre
 * cuentas propias, una devolución— entró al banco pero no es plata de un
 * cliente, y contarlo inflaría «entró hoy».
 */
const descartado = (m: Movimiento) => m.origen === "humano" && !esDeCerteza(m.estado);

const suma = (ms: Movimiento[]) => ms.reduce((s, m) => s + (m.montoCentavos ?? 0), 0);
const deHoy = (m: Movimiento, hoy: string) => String(m.fecha ?? "").slice(0, 10) === hoy;

export function cifrasDeHoy(libro: Libro, verCartera: boolean, hoy = hoyLocal()): CifrasDeHoy {
  const entraron = (libro.movimientos ?? []).filter((m) => deHoy(m, hoy) && !descartado(m));
  const pendientes = libro.pendientes ?? [];
  return {
    entro: { centavos: suma(entraron), pagos: entraron.length },
    faltaDecir: {
      centavos: suma(pendientes),
      pagos: pendientes.length,
      deHoy: pendientes.filter((m) => deHoy(m, hoy)).length,
    },
    faltaCobrar: verCartera
      ? (libro.facturas ?? []).reduce((s, f) => s + Math.max(0, f.saldoCentavos), 0)
      : null,
  };
}
